import { useEffect, useMemo, useRef, useState } from 'react';
import { Check, ChevronRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Badge } from '@/components/Shared/Badge';
import { EmployeeAvatar } from '@/components/Shared/EmployeeAvatar';
import { EmptyState } from '@/components/Shared/EmptyState';
import { UtilizationBar } from '@/components/Shared/UtilizationBar';
import { usePlanningAssistantStore } from '@/store/usePlanningAssistantStore';
import {
  firstName,
  fitsForEmployee,
  fmtHours,
  fmtHoursValue,
  fmtRange,
  ghostOf,
  isAvailable,
  longestGap,
  objectMap,
  taskById,
  unavailableText,
} from './assistantFormat';
import type { AssistantEmployee, AssistantTask, SuggestionFit, SuggestionsDto } from './assistantTypes';
import { AssistantMap } from './components/AssistantMap';
import { DayLine } from './components/DayLine';
import { StartPointDialog } from './components/StartPointDialog';
import { SuggestionCard } from './components/SuggestionCard';

const VISIBLE_SUGGESTIONS = 4;

interface Props {
  data: SuggestionsDto;
}

/**
 * Stable stepper order: available employees by free time (desc) as of the first load of the
 * day. why: re-sorting after every assignment would move the current employee under the cursor.
 */
function useStableOrder(data: SuggestionsDto): AssistantEmployee[] {
  const orderRef = useRef<{ date: string | undefined; ids: string[] }>({ date: undefined, ids: [] });
  const available = (data.employees ?? []).filter(isAvailable);
  const known = orderRef.current.date === data.date ? orderRef.current.ids : [];
  const fresh = available
    .filter((e) => !known.includes(e.employeeId ?? ''))
    .sort((a, b) => (b.freeMinutes ?? 0) - (a.freeMinutes ?? 0))
    .map((e) => e.employeeId ?? '');
  const ids = [...known.filter((id) => available.some((e) => e.employeeId === id)), ...fresh];
  orderRef.current = { date: data.date, ids };
  return ids
    .map((id) => available.find((e) => e.employeeId === id))
    .filter((e): e is AssistantEmployee => !!e);
}

export function EmployeeMode({ data }: Props) {
  const { t, i18n } = useTranslation('planning');
  const lang = i18n.language;
  const employeeIndex = usePlanningAssistantStore((s) => s.employeeIndex);
  const setEmployeeIndex = usePlanningAssistantStore((s) => s.setEmployeeIndex);
  const doneEmployeeIds = usePlanningAssistantStore((s) => s.doneEmployeeIds);
  const markEmployeeDone = usePlanningAssistantStore((s) => s.markEmployeeDone);
  const newlyAssignedTaskIds = usePlanningAssistantStore((s) => s.newlyAssignedTaskIds);
  const assign = usePlanningAssistantStore((s) => s.assign);
  const busy = usePlanningAssistantStore((s) => s.busy);
  const setStartPoint = usePlanningAssistantStore((s) => s.setStartPoint);
  const storeDate = usePlanningAssistantStore((s) => s.date);
  const day = data.date ?? storeDate;

  const order = useStableOrder(data);
  const unavailable = (data.employees ?? []).filter((e) => !isAvailable(e));
  const objects = useMemo(() => objectMap(data), [data]);

  const idx = Math.min(employeeIndex, Math.max(0, order.length - 1));
  const employee = order[idx];
  const employeeId = employee?.employeeId ?? '';

  const [activeTaskId, setActiveTaskId] = useState<string | null>(null);
  const [showAll, setShowAll] = useState(false);
  const [hoverBlockId, setHoverBlockId] = useState<string | null>(null);
  const [startDialogOpen, setStartDialogOpen] = useState(false);
  useEffect(() => {
    setActiveTaskId(null);
    setShowAll(false);
    setHoverBlockId(null);
    setStartDialogOpen(false);
  }, [employeeId]);

  const suggestions = useMemo(
    () =>
      fitsForEmployee(data, employeeId)
        .map((fit) => ({ fit, task: taskById(data, fit.taskId) }))
        .filter((s): s is { fit: SuggestionFit; task: AssistantTask } => !!s.task),
    [data, employeeId],
  );
  const shown = showAll ? suggestions : suggestions.slice(0, VISIBLE_SUGGESTIONS);
  const active = suggestions.find((s) => s.task.taskId === activeTaskId) ?? null;

  const goNext = () => {
    if (!employee) return;
    markEmployeeDone(employeeId);
    setEmployeeIndex(Math.min(idx + 1, order.length - 1));
  };

  // Keyboard: ↑/↓ choose a suggestion, Enter plans it, → next employee.
  const keyState = useRef({ shown, active, goNext, assign, busy, dialogOpen: startDialogOpen });
  keyState.current = { shown, active, goNext, assign, busy, dialogOpen: startDialogOpen };
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      // The start point dialog owns the keyboard while it is open.
      if (keyState.current.dialogOpen) return;
      const target = e.target as HTMLElement | null;
      if (target && (target.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName))) {
        return;
      }
      // A focused button or link handles Enter itself.
      if (e.key === 'Enter' && target && ['BUTTON', 'A'].includes(target.tagName)) return;
      const { shown: list, active: current, goNext: next, assign: plan, busy: isBusy } = keyState.current;
      const pos = current ? list.findIndex((s) => s.task.taskId === current.task.taskId) : -1;
      if (e.key === 'ArrowDown' && list.length) {
        e.preventDefault();
        setActiveTaskId(list[Math.min(list.length - 1, pos + 1)].task.taskId ?? null);
      } else if (e.key === 'ArrowUp' && list.length) {
        e.preventDefault();
        setActiveTaskId(list[Math.max(0, pos - 1)].task.taskId ?? null);
      } else if (e.key === 'Enter' && current && !isBusy) {
        e.preventDefault();
        void plan(current.fit);
      } else if (e.key === 'ArrowRight') {
        next();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  if (!employee) {
    return (
      <div className="bg-white border border-slate-200 rounded-xl">
        <EmptyState title={t('assistant.employeeMode.noEmployees')} />
      </div>
    );
  }

  const name = firstName(employee.name);
  const start = employee.startPoint;
  const isOverride = start?.source === 'Override';
  const startLabel =
    start?.source === 'Override'
      ? start.label || t('assistant.startPoint.customStart')
      : start?.source === 'Home'
        ? t('assistant.startPoint.home')
        : start?.source === 'FirstObject'
          ? t('assistant.startPoint.firstObject', { label: start.label ?? '' })
          : t('assistant.startPoint.unknown');
  const isLast = idx >= order.length - 1;
  const markers = (data.tasks ?? []).map((task) => {
    const rank = shown.findIndex((s) => s.task.taskId === task.taskId);
    return { task, rank: rank >= 0 ? rank + 1 : null, faded: rank < 0 };
  });

  return (
    <div className="flex flex-col gap-4 lg:h-full min-h-0">
      {/* Stepper */}
      <div className="flex items-center gap-1.5 overflow-x-auto pb-1 shrink-0">
        {order.map((e, i) => {
          const isActive = i === idx;
          const free = e.freeMinutes ?? 0;
          return (
            <button
              key={e.employeeId}
              type="button"
              onClick={() => setEmployeeIndex(i)}
              className={`flex items-center gap-2 pl-1 pr-3 py-1 rounded-full border bg-white text-[12px] whitespace-nowrap ${
                isActive ? 'border-slate-900 text-slate-900' : 'border-slate-200 text-slate-600 hover:border-slate-300'
              }`}
            >
              <span className="relative">
                <EmployeeAvatar employee={{ name: e.name, avatarColor: e.avatarColor }} size={24} />
                {doneEmployeeIds.includes(e.employeeId ?? '') && (
                  <span className="absolute -right-1 -bottom-1 w-3.5 h-3.5 rounded-full bg-emerald-500 text-white grid place-items-center border border-white">
                    <Check size={8} strokeWidth={3.5} />
                  </span>
                )}
              </span>
              <span className="font-medium">{firstName(e.name)}</span>
              <span className={`tabular-nums ${free >= 60 ? 'text-orange-600' : 'text-slate-400'}`}>
                {free >= 15 ? t('assistant.hoursFree', { hours: fmtHours(free, lang) }) : t('assistant.full')}
              </span>
            </button>
          );
        })}
        {unavailable.map((e) => (
          <span key={e.employeeId} className="ml-2 text-[11px] text-slate-400 whitespace-nowrap">
            {e.name} · {unavailableText(t, e)}
          </span>
        ))}
      </div>

      <div className="grid gap-4 flex-1 min-h-0 grid-cols-1 lg:grid-cols-[minmax(0,1fr)_minmax(340px,420px)]">
        {/* Left: employee, day line, map */}
        <div className="bg-white border border-slate-200 rounded-xl p-4 flex flex-col gap-4 min-h-0">
          <div className="flex items-center gap-3 flex-wrap">
            <EmployeeAvatar employee={{ name: employee.name, avatarColor: employee.avatarColor }} size={40} />
            <div className="flex-1 min-w-[180px]">
              <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-slate-400 whitespace-nowrap">
                {t('assistant.employeeMode.kicker', { index: idx + 1, total: order.length })}
              </div>
              <div className="text-[18px] font-semibold tracking-tight text-slate-900">{employee.name}</div>
              <div className="text-[12px] text-slate-500 tabular-nums">
                {t('assistant.employeeMode.workTime', { range: fmtRange(employee.workStart, employee.workEnd) })} ·{' '}
                {t('assistant.employeeMode.assignments', { count: employee.blocks?.length ?? 0 })}
              </div>
              <div className="flex items-center gap-1.5 flex-wrap text-[12px] text-slate-500">
                <span className="truncate max-w-[260px]">{t('assistant.startPoint.line', { label: startLabel })}</span>
                {isOverride && <Badge variant="info">{t('assistant.startPoint.adjusted')}</Badge>}
                <button
                  type="button"
                  onClick={() => setStartDialogOpen(true)}
                  disabled={busy}
                  className="text-slate-600 underline underline-offset-2 hover:text-slate-900 disabled:opacity-40 disabled:no-underline"
                >
                  {t('assistant.startPoint.change')}
                </button>
                {isOverride && (
                  <button
                    type="button"
                    onClick={() => void setStartPoint(employeeId, null)}
                    disabled={busy}
                    className="text-slate-600 underline underline-offset-2 hover:text-slate-900 disabled:opacity-40 disabled:no-underline"
                  >
                    {t('assistant.startPoint.reset')}
                  </button>
                )}
              </div>
            </div>
            <div className="w-[180px] shrink-0 flex flex-col gap-1.5 items-end">
              <div className="flex items-baseline gap-1">
                <span className="text-[24px] font-semibold font-mono tabular-nums text-slate-900">
                  {fmtHoursValue(employee.freeMinutes, lang)}
                </span>
                <span className="text-[12px] text-slate-500">{t('assistant.employeeMode.hoursFreeUnit')}</span>
              </div>
              <div className="w-full">
                <UtilizationBar planned={employee.plannedMinutes ?? 0} target={employee.targetMinutes ?? 0} height={4} />
              </div>
              <span className="text-[11px] text-slate-500 tabular-nums">
                {t('assistant.employeeMode.planned', {
                  planned: fmtHours(employee.plannedMinutes, lang),
                  target: fmtHours(employee.targetMinutes, lang),
                })}
              </span>
            </div>
          </div>

          <DayLine
            employee={employee}
            ghost={ghostOf(active?.fit, active?.task)}
            ticks
            newlyAssignedTaskIds={newlyAssignedTaskIds}
            onBlockHover={setHoverBlockId}
            highlightedBlockId={hoverBlockId}
          />

          <AssistantMap
            className="flex-1 min-h-[260px]"
            routes={[{ employee }]}
            markers={markers}
            objects={objects}
            ghost={active ? { ...active, employee } : null}
            selectedTaskId={activeTaskId}
            onTaskClick={(taskId) => setActiveTaskId(taskId === activeTaskId ? null : taskId)}
            highlightedStopId={hoverBlockId}
            onStopHover={setHoverBlockId}
            fitIgnoresFaded
          />
        </div>

        {/* Right: suggestions */}
        <div className="bg-white border border-slate-200 rounded-xl flex flex-col min-h-0 max-h-[720px] lg:max-h-none">
          <div className="px-4 pt-4 pb-3 border-b border-slate-100">
            <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-slate-400">
              {t('assistant.employeeMode.suggestionsKicker')}
            </div>
            <div className="text-[15px] font-semibold text-slate-900">
              {t('assistant.employeeMode.suggestionsTitle', { name })}
            </div>
            <div className="text-[12px] text-slate-500">{t('assistant.employeeMode.suggestionsSub')}</div>
          </div>

          <div className="flex-1 overflow-y-auto p-3 flex flex-col gap-2">
            {(employee.freeMinutes ?? 0) < 15 && suggestions.length === 0 && (
              <div className="m-auto text-center text-[13px] text-slate-500 flex flex-col items-center gap-2 py-6">
                <span className="w-9 h-9 rounded-full bg-emerald-50 text-emerald-600 grid place-items-center">
                  <Check size={18} strokeWidth={2} />
                </span>
                {t('assistant.employeeMode.fullyPlanned', { name })}
              </div>
            )}
            {(employee.freeMinutes ?? 0) >= 15 && suggestions.length === 0 && (
              <div className="m-auto text-center text-[13px] text-slate-500 max-w-[280px] py-6">
                {t('assistant.employeeMode.noFit', { hours: fmtHours(longestGap(employee), lang) })}
              </div>
            )}
            {shown.map((s, i) => (
              <SuggestionCard
                key={s.task.taskId}
                fit={s.fit}
                task={s.task}
                rank={i + 1}
                objects={objects}
                active={activeTaskId === s.task.taskId}
                disabled={busy}
                onHover={setActiveTaskId}
                startSource={employee.startPoint?.source}
                onAssign={(fit) => void assign(fit)}
              />
            ))}
            {suggestions.length > VISIBLE_SUGGESTIONS && (
              <button
                type="button"
                onClick={() => setShowAll((v) => !v)}
                className="text-[12px] text-slate-500 hover:text-slate-900 py-1 tabular-nums"
              >
                {showAll
                  ? t('assistant.employeeMode.less')
                  : t('assistant.employeeMode.more', { count: suggestions.length - VISIBLE_SUGGESTIONS })}
              </button>
            )}
          </div>

          <div className="p-3 border-t border-slate-100 flex items-center gap-2">
            <button
              type="button"
              onClick={() => setEmployeeIndex(Math.max(0, idx - 1))}
              disabled={idx === 0}
              className="px-3 py-1.5 rounded-md border border-slate-200 text-[12px] text-slate-600 hover:bg-slate-50 disabled:opacity-40"
            >
              {t('assistant.employeeMode.back')}
            </button>
            <div className="flex-1" />
            {!isLast ? (
              <button
                type="button"
                onClick={goNext}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-slate-900 text-white text-[12px] font-medium"
              >
                {t('assistant.employeeMode.nextTo', { name: firstName(order[idx + 1]?.name) })}
                <ChevronRight size={14} strokeWidth={2} />
              </button>
            ) : (
              <button
                type="button"
                onClick={() => markEmployeeDone(employeeId)}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-slate-900 text-white text-[12px] font-medium"
              >
                <Check size={14} strokeWidth={2} />
                {t('assistant.employeeMode.finishDay')}
              </button>
            )}
          </div>
        </div>
      </div>

      <StartPointDialog
        isOpen={startDialogOpen}
        onClose={() => setStartDialogOpen(false)}
        employee={employee}
        date={day}
        objects={objects}
      />
    </div>
  );
}
